import { blobToDataURL, resizeImageFile } from '@nx-smartmonkey/shared/helpers';
import heic2any from 'heic2any';
import React, { useState } from 'react';

import { RoutalImageCropDialog } from './RoutalImageCropDialog';

export type RoutalImageUploadData = {
  fileName: string;
  imageType: string;
  image: string;
};

export const ROUTAL_IMAGE_UPLOAD_ID = `routal-image-upload-id`;

export type RoutalImageUploadProps = {
  id?: string;
  crop?: boolean;
  cropShape?: `rect` | `round`;
  cropDialogTitle?: React.ReactElement | string;
  confirmText?: React.ReactElement | string;
  cancelText?: React.ReactElement | string;
  fileName: string;
  maxFileSize: number;
  aspect?: number;
  capture?: boolean | `environment` | `user`;
  isMobile?: boolean;
  onImageUploaded: (...args: any[]) => any;
  setIsLoading?: (isLoading: boolean) => void;
};

export const RoutalImageUpload = ({
  id = ROUTAL_IMAGE_UPLOAD_ID,
  crop = false,
  cropShape,
  cropDialogTitle,
  confirmText,
  cancelText,
  fileName,
  maxFileSize,
  aspect,
  capture,
  isMobile = false,
  onImageUploaded,
  setIsLoading,
}: RoutalImageUploadProps) => {
  const [cropDialogOpen, setCropDialogOpen] = useState<boolean>(false);
  const [imageToCrop, setImageToCrop] = useState<RoutalImageUploadData | undefined>(undefined);

  const handleHeicFiles = async (imageType: string, blob: Blob) => {
    if (imageType === `image/heic` || imageType === `image/heif`) {
      setIsLoading && setIsLoading(true);

      const conversionResult = await heic2any({
        blob,
        toType: `image/jpeg`,
        quality: 0.5,
      });

      return conversionResult as Blob;
    }

    return blob;
  };

  const handleFile = async (files?: FileList | null) => {
    if (files && files.length > 0) {
      const file = files[0];
      const fileToSend = await resizeImageFile(file, maxFileSize);
      const imageType = file.type ?? `image/jpeg`;
      let blob = new Blob([fileToSend], { type: imageType });

      blob = await handleHeicFiles(imageType, blob);

      if (crop) {
        const base64 = await blobToDataURL(blob);
        setCropDialogOpen(true);
        setImageToCrop({
          fileName,
          imageType,
          image: base64 as string,
        });
      } else {
        onHandleUpload(fileName, blob.type, blob);
      }
    } else {
      setIsLoading && setIsLoading(false);
    }
  };

  const onHandleUpload = async (fileName: string, imageType: string, image: Blob) => {
    onImageUploaded(fileName, imageType, image);
    setIsLoading && setIsLoading(false);
    setCropDialogOpen(false);
  };

  return (
    <>
      <input
        type="file"
        id={id}
        accept="image/png, image/jpeg, image/svg+xml, image/gif, image/webp"
        style={{ display: `none` }}
        capture={capture}
        onChange={(e) => handleFile(e.target.files)}
      />

      <RoutalImageCropDialog
        title={cropDialogTitle}
        aspect={aspect}
        cropShape={cropShape}
        open={cropDialogOpen}
        image={imageToCrop?.image}
        imageType={imageToCrop?.imageType}
        confirmText={confirmText}
        cancelText={cancelText}
        isMobile={isMobile}
        onCloseDialog={() => {
          setCropDialogOpen(false);
          setImageToCrop(undefined);
        }}
        onCrop={(image: Blob | null) => {
          if (image) {
            onHandleUpload(imageToCrop!.fileName, image.type, image);
          }
        }}
      />
    </>
  );
};
